/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import RcMenu, { MenuProps as RcMenuProps } from "rc-menu";
import React, { useEffect, useRef } from "react";

import "./Menu.scss";

export type IMenuProps = RcMenuProps;

const OVERFLOW_LI = "li.Menu-overflowed-submenu";
const POPUP_ITEM = "li.Menu-item:not(.Menu-item-disabled)";

const syncOverflowTabIndex = (container: Element) => {
  const items = container.querySelectorAll(OVERFLOW_LI);
  items.forEach((li) => {
    const title = li.querySelector(".Menu-submenu-title");
    if (!title) return;
    const style = (li as HTMLElement).style;
    const hidden =
      style.display === "none" ||
      style.visibility === "hidden" ||
      style.position === "absolute";
    title.setAttribute("tabindex", hidden ? "-1" : "0");
  });
};

// The overflow popup ul has an ID containing "overflowed-indicator"
// It's rendered in a portal outside the menu DOM tree, so we search the whole document.
const findOverflowPopupMenu = (): HTMLElement | null => {
  const all = document.querySelectorAll<HTMLElement>(
    'ul[id*="overflowed-indicator"]',
  );
  for (const el of all) {
    if (el.offsetHeight > 0) return el;
  }
  return null;
};

const openOverflowPopup = (triggerTitle: HTMLElement) => {
  triggerTitle.click();

  // Portal renders asynchronously — retry a few frames until it appears
  let attempts = 5;
  const tryFocus = () => {
    const popupMenu = findOverflowPopupMenu();
    if (!popupMenu) {
      if (--attempts > 0) requestAnimationFrame(tryFocus);
      return;
    }

    const items = popupMenu.querySelectorAll<HTMLElement>(POPUP_ITEM);
    items[0]?.focus();

    const onPopupKeyDown = (e: KeyboardEvent) => {
      const allItems = Array.from(
        popupMenu.querySelectorAll<HTMLElement>(POPUP_ITEM),
      );
      const idx = allItems.indexOf(document.activeElement as HTMLElement);

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          allItems[idx < allItems.length - 1 ? idx + 1 : 0]?.focus();
          break;
        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          allItems[idx > 0 ? idx - 1 : allItems.length - 1]?.focus();
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          (document.activeElement as HTMLElement)?.click();
          popupMenu.removeEventListener("keydown", onPopupKeyDown);
          triggerTitle.click();
          triggerTitle.focus();
          break;
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          triggerTitle.click();
          triggerTitle.focus();
          popupMenu.removeEventListener("keydown", onPopupKeyDown);
          break;
        case "Tab":
          e.preventDefault();
          e.stopPropagation();
          triggerTitle.click();
          triggerTitle.focus();
          popupMenu.removeEventListener("keydown", onPopupKeyDown);
          break;
      }
    };

    popupMenu.addEventListener("keydown", onPopupKeyDown);
  };

  requestAnimationFrame(tryFocus);
};

export const Menu: React.FC<IMenuProps> = ({ children, ...props }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const menuElement = containerRef.current?.querySelector("ul.Menu");
    if (!menuElement) return;

    // rc-menu forces tabIndex=0 on the container, override it after render
    menuElement.setAttribute("tabindex", "-1");

    syncOverflowTabIndex(menuElement);

    const observer = new MutationObserver(() => {
      syncOverflowTabIndex(menuElement);
    });
    observer.observe(menuElement, {
      attributes: true,
      attributeFilter: ["style"],
      subtree: true,
    });

    // rc-menu doesn't attach onKeyDown to submenu titles — it relies on
    // internal "active" state delegation. Add a delegated handler so
    // Enter/Space on the focused overflow "..." button opens the popup
    // and moves focus into it.
    const onKeyDown = (e: Event) => {
      const ke = e as KeyboardEvent;
      if (ke.key !== "Enter" && ke.key !== " ") return;
      const target = ke.target as HTMLElement;
      if (!target.closest(OVERFLOW_LI)) return;
      if (!target.classList.contains("Menu-submenu-title")) return;
      ke.preventDefault();
      openOverflowPopup(target);
    };
    menuElement.addEventListener("keydown", onKeyDown);

    return () => {
      observer.disconnect();
      menuElement.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return (
    <div ref={containerRef} className="Menu-wrapper">
      <RcMenu prefixCls="Menu" {...props}>
        {children}
      </RcMenu>
    </div>
  );
};
